import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useAuth } from '../auth/AuthContext'
import { isAdminUser } from '../auth/authorization'
import { useI18n } from '../i18n/context'
import { sanitizeEmailInput, sanitizePasswordInput } from '../runtime/inputValidation'
import {
  OPEN_ALBUM_SELECTION_EVENT,
  OPEN_HELP_EVENT,
  OPEN_HISTORY_EVENT,
  OPEN_THEME_REVIEW_EVENT
} from '../app/events'
import { PublicPageContent } from './components/PublicPageContent'
import { WebsiteSettingsOverlay } from './components/WebsiteSettingsOverlay'
import { AuthPanel } from './components/AuthPanel'
import settingsDarkIcon from '../assets/icons/dark/settings.png'
import settingsLightIcon from '../assets/icons/light/settings.png'

type Mode = 'signIn' | 'signUp' | 'magicLink' | 'forgotPassword' | 'resetPassword'
type InfoPage = 'home' | 'howto' | 'about' | 'contact' | 'terms'
type EntryMode = 'auth' | 'checkAlbums'

const THEME_MODE_KEY = 'investalbum-theme-mode-v1'
const MAX_EMAIL_LENGTH = 254
const MAX_PASSWORD_LENGTH = 128
const INFO_PAGE_PATHS: Record<InfoPage, string> = {
  home: '/',
  howto: '/how-to-use',
  about: '/about-us',
  contact: '/contact',
  terms: '/terms'
}

export function infoPageFromPathname(pathname: string): InfoPage {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/'
  const match = Object.entries(INFO_PAGE_PATHS).find(([, path]) => path === normalizedPath)
  return (match?.[0] as InfoPage | undefined) ?? 'home'
}

export function AuthScreen({
  entryMode = 'auth',
  onCheckAlbums
}: {
  entryMode?: EntryMode
  onCheckAlbums?: () => void
}): JSX.Element {
  const {
    status,
    user,
    signOut,
    signInWithPassword,
    signUp,
    signInWithMagicLink,
    requestPasswordReset,
    updatePassword,
    shouldResetPassword
  } = useAuth()
  const { t, locale, setLocale } = useI18n()
  const isAdmin = status === 'signedIn' && isAdminUser(user)
  const [mode, setMode] = useState<Mode>(shouldResetPassword ? 'resetPassword' : 'signIn')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [infoPage, setInfoPage] = useState<InfoPage>(() =>
    infoPageFromPathname(window.location.pathname)
  )
  const [showAuthForm, setShowAuthForm] = useState(shouldResetPassword)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    try {
      const storedTheme = window.localStorage.getItem(THEME_MODE_KEY)
      return storedTheme === null ? true : storedTheme === 'dark'
    } catch {
      return true
    }
  })
  const settingsMenuRef = useRef<HTMLDivElement>(null)
  const settingsIcon = isDarkMode ? settingsDarkIcon : settingsLightIcon

  useEffect(() => {
    if (shouldResetPassword) {
      setMode('resetPassword')
      setShowAuthForm(true)
      setError(null)
      setNotice(null)
    }
  }, [shouldResetPassword])

  useEffect(() => {
    function handlePopState(): void {
      setInfoPage(infoPageFromPathname(window.location.pathname))
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  useEffect(() => {
    const cls = 'theme-dark'
    document.body.classList.toggle(cls, isDarkMode)
    try {
      window.localStorage.setItem(THEME_MODE_KEY, isDarkMode ? 'dark' : 'light')
    } catch {
      // non-fatal
    }
  }, [isDarkMode])

  useEffect(() => {
    const noBackgroundClass = 'public-home-flat'
    document.body.classList.toggle(noBackgroundClass, infoPage === 'home')
    return () => {
      document.body.classList.remove(noBackgroundClass)
    }
  }, [infoPage])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target as Node)) {
        setIsSettingsOpen(false)
      }
    }
    function handleEsc(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setIsSettingsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEsc)
    }
  }, [])

  function changeMode(nextMode: Mode): void {
    setMode(nextMode)
    setShowAuthForm(true)
    setError(null)
    setNotice(null)
  }

  function openInfoPage(page: InfoPage): void {
    setInfoPage(page)
    const path = INFO_PAGE_PATHS[page]
    if (window.location.pathname !== path) {
      window.history.pushState(null, '', path)
    }
  }

  function openAuth(nextMode: Mode): void {
    setMode(shouldResetPassword ? 'resetPassword' : nextMode)
    setShowAuthForm(true)
    setError(null)
    setNotice(null)
    setIsSettingsOpen(false)
  }

  function openInternalAction(eventName: string): void {
    if (!onCheckAlbums) return
    onCheckAlbums()
    window.setTimeout(() => {
      window.dispatchEvent(new Event(eventName))
    }, 0)
    setIsSettingsOpen(false)
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    const normalizedEmail = sanitizeEmailInput(email, MAX_EMAIL_LENGTH)
    const normalizedPassword = sanitizePasswordInput(password, MAX_PASSWORD_LENGTH)
    if (normalizedEmail !== email) {
      setEmail(normalizedEmail)
    }
    if (normalizedPassword !== password) {
      setPassword(normalizedPassword)
    }
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      if (mode === 'magicLink') {
        const { error: err } = await signInWithMagicLink(normalizedEmail)
        if (err) setError(err)
        else setNotice(t.authMagicLinkSent)
        return
      }
      if (mode === 'forgotPassword') {
        const { error: err } = await requestPasswordReset(normalizedEmail)
        if (err) setError(err)
        else setNotice(t.authPasswordResetSent)
        return
      }
      if (mode === 'resetPassword') {
        if (normalizedPassword !== confirmPassword) {
          setError(t.authPasswordMismatch)
          return
        }
        const { error: err } = await updatePassword(normalizedPassword)
        if (err) setError(err)
        else {
          setNotice(t.authPasswordUpdated)
          setPassword('')
          setConfirmPassword('')
          setMode('signIn')
        }
        return
      }
      const action = mode === 'signUp' ? signUp : signInWithPassword
      const { error: err } = await action(normalizedEmail, normalizedPassword)
      if (err) setError(err)
      else if (mode === 'signUp') setNotice(t.authConfirmEmail)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`auth-screen ${infoPage === 'home' ? 'no-main-background' : ''}`}>
      <div className="settings-fab" ref={settingsMenuRef}>
        <button
          className="settings-fab-button"
          type="button"
          aria-label={t.settings}
          title={t.settings}
          onClick={() => setIsSettingsOpen((value) => !value)}
        >
          <img src={settingsIcon} alt="" aria-hidden="true" className="settings-fab-icon" />
        </button>
        {isSettingsOpen && (
          <WebsiteSettingsOverlay
            t={t}
            status={status}
            userEmail={user?.email}
            isAdmin={isAdmin}
            locale={locale}
            setLocale={setLocale}
            isDarkMode={isDarkMode}
            setIsDarkMode={setIsDarkMode}
            setInfoPage={openInfoPage}
            openInternalAction={openInternalAction}
            openAuth={openAuth}
            closeOverlay={() => setIsSettingsOpen(false)}
            signOut={() => void signOut()}
            openHistoryEvent={OPEN_HISTORY_EVENT}
            openAlbumSelectionEvent={OPEN_ALBUM_SELECTION_EVENT}
            openHelpEvent={OPEN_HELP_EVENT}
            openThemeReviewEvent={OPEN_THEME_REVIEW_EVENT}
          />
        )}
      </div>
      <div className="auth-layout">
        <header className="auth-top-header" aria-label={t.authSubtitle}>
          <AuthPanel
            t={t}
            isDarkMode={isDarkMode}
            mode={mode}
            busy={busy}
            email={email}
            password={password}
            confirmPassword={confirmPassword}
            error={error}
            notice={notice}
            showAuthForm={showAuthForm}
            entryMode={entryMode}
            infoPage={infoPage}
            setInfoPage={openInfoPage}
            maxEmailLength={MAX_EMAIL_LENGTH}
            maxPasswordLength={MAX_PASSWORD_LENGTH}
            setEmail={setEmail}
            setPassword={setPassword}
            setConfirmPassword={setConfirmPassword}
            changeMode={changeMode}
            openAuth={openAuth}
            onCheckAlbums={onCheckAlbums}
            onSubmit={handleSubmit}
          />
        </header>

        <PublicPageContent
          t={t}
          locale={locale}
          isDarkMode={isDarkMode}
          infoPage={infoPage}
          setInfoPage={openInfoPage}
        />
      </div>
    </div>
  )
}
